import React, { useEffect, useRef } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import clsx from 'clsx';

import MultiProductPreview from './MultiProductReview';
import ProductSkeleton from './ProductSkeleton';
import styles from './productPreviewSection.module.css';
import IconContainer from '../../../common/IconContainer/IconContainer';
import { useScrollShadow } from '../../../../hooks/useScrollShadow';
import useIsMobile from '../../../../hooks/useIsMobile';

interface Props {
  products: any[];
  selectedProductsForOrder: any[];
  allOptions: object;
  defaultCurrency: string;
  maxQuantities: object;
  quantities: object;
  setQuantities: React.Dispatch<React.SetStateAction<object>>;
  setIsScrolling: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
}

export default function ProductsPreviewSection({
  products,
  selectedProductsForOrder,
  allOptions,
  defaultCurrency,
  maxQuantities,
  quantities,
  setQuantities,
  setIsScrolling,
  isLoading = false,
}: Props) {
  const scrollColumnRef = useRef(null);
  const isScrollShadow = useScrollShadow(scrollColumnRef);
  const isMobile = useIsMobile();

  useEffect(() => {
    setIsScrolling(isScrollShadow);
  }, [isScrollShadow, setIsScrolling]);

  const reminderBaseCount = isMobile ? 2 : 3;

  const remainder = products.length % reminderBaseCount;
  let skeletonCount = 0;
  if (isLoading) {
    skeletonCount = reminderBaseCount;
  } else if (remainder !== 0) {
    skeletonCount = reminderBaseCount - remainder;
  }

  if (products.length === 0 && !isLoading) {
    return (
      <div className={styles.ftuxContainer}>
        <div className="flex flex-col w-full flex-center items-center">
          <IconContainer icon="🎁" />
          <p
            className={clsx('text-xl mt-6 mb-2 font-medium text-default text-center', {
              'text-base': isMobile,
            })}
          >
            No Products Available!
          </p>
          <p className="text-sm text-subdued text-center">
            Currently, there are no products available for you to request.
            {!isMobile ? <br /> : ' '}
            Check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.multiProductsPreview} ${products?.length > 3 && styles.highProducts}`}
      ref={scrollColumnRef}
    >
      {products.length !== 0 &&
        products.map((product, i) => {
          const { productDetails } = product;
          const productForOptions = selectedProductsForOrder?.[i];
          return (
            <MultiProductPreview
              key={i}
              product={product}
              productDetails={productDetails}
              productForOptions={productForOptions}
              allOptions={allOptions}
              defaultCurrency={defaultCurrency}
              maxQuantities={maxQuantities}
              quantities={quantities}
              setQuantities={setQuantities}
            />
          );
        })}
      {Array.from({ length: skeletonCount }, (_, index) => (
        <ProductSkeleton
          key={`skeleton-${index}`}
          color={isLoading ? 'var(--background-highlight)' : 'var(--background-default)'}
        />
      ))}
    </div>
  );
}
